import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { invalidateFor } from "@/lib/cache-registry";

const BUCKET = "task-attachments";

export interface TaskComment {
  id: string;
  assignment_id: string;
  user_id: string;
  body: string | null;
  attachment_url: string | null;
  attachment_name: string | null;
  attachment_type: string | null;
  attachment_size_bytes: number | null;
  created_at: string;
  user: {
    full_name: string;
    avatar_url: string | null;
  } | null;
}

interface CommentRow {
  id: string;
  assignment_id: string;
  user_id: string;
  body: string | null;
  attachment_url: string | null;
  attachment_name: string | null;
  attachment_type: string | null;
  attachment_size_bytes: number | null;
  created_at: string;
  users: { full_name: string; avatar_url: string | null } | null;
}

/**
 * Henter kommentartråden for én oppgave (budget_line_assignment).
 * Realtime håndteres sentralt via useRealtimeListeners (REALTIME_TABLES).
 */
export function useTaskComments(assignmentId: string | null) {
  const queryKey = useMemo(() => ["task-comments", assignmentId], [assignmentId]);

  const query = useQuery({
    queryKey,
    enabled: !!assignmentId,
    queryFn: async (): Promise<TaskComment[]> => {
      if (!assignmentId) return [];
      const { data, error } = await supabase
        .from("task_comments")
        .select(
          "id, assignment_id, user_id, body, attachment_url, attachment_name, attachment_type, attachment_size_bytes, created_at, users:user_id(full_name, avatar_url)",
        )
        .eq("assignment_id", assignmentId)
        .is("deleted_at", null)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data ?? []).map((row: unknown) => {
        const r = row as CommentRow;
        return {
          id: r.id,
          assignment_id: r.assignment_id,
          user_id: r.user_id,
          body: r.body,
          attachment_url: r.attachment_url,
          attachment_name: r.attachment_name,
          attachment_type: r.attachment_type,
          attachment_size_bytes: r.attachment_size_bytes,
          created_at: r.created_at,
          user: r.users ? { full_name: r.users.full_name, avatar_url: r.users.avatar_url } : null,
        };
      });
    },
  });

  return query;
}

interface CreateInput {
  assignmentId: string;
  userId: string;
  body?: string;
  file?: File | null;
}

async function uploadAttachment(file: File, assignmentId: string, userId: string) {
  const ext = file.name.split(".").pop() ?? "bin";
  const fileName = `${assignmentId}/${userId}-${Date.now()}.${ext}`;
  const { error: upErr } = await supabase.storage.from(BUCKET).upload(fileName, file, {
    cacheControl: "3600",
    upsert: false,
    contentType: file.type || undefined,
  });
  if (upErr) throw upErr;
  // WP4: bucket is private. Store the storage PATH (not a public URL); the
  // viewer resolves a short-lived signed URL on demand (useSignedAttachmentUrl).
  return {
    url: fileName,
    name: file.name,
    type: file.type || "application/octet-stream",
    size: file.size,
  };
}

/**
 * Normaliserer en lagret attachment_url til en ren storage-path.
 * Nye rader (etter WP4) lagrer pathen direkte — det normale tilfellet, og vi
 * returnerer da verdien uendret. Eldre rader lagret en full public-URL; for dem
 * trekker vi ut delen etter Supabase sin object-markør («/object/public/<bucket>/»
 * eller «/object/sign/<bucket>/»), med fallback til «/<bucket>/», så de også kan
 * signeres når bucketen nå er privat.
 */
function toStoragePath(value: string): string {
  // Allerede en ren storage-path (det normale tilfellet etter WP4).
  if (!value.includes("://")) return value;

  // Legacy full-URL: finn første kjente markør som peker inn i bucketen.
  const markers = [`/object/public/${BUCKET}/`, `/object/sign/${BUCKET}/`, `/${BUCKET}/`];
  for (const marker of markers) {
    const idx = value.indexOf(marker);
    if (idx !== -1) return value.slice(idx + marker.length).replace(/^\/+/, "");
  }
  return value;
}

/**
 * WP4: task-attachments-bucket er privat. Resolver en signert URL for et
 * vedlegg (path lagret i attachment_url). Returnerer null hvis signing feiler.
 */
export async function getTaskAttachmentUrl(value: string): Promise<string | null> {
  if (!value) return null;
  const { data, error } = await supabase.storage
    .from(BUCKET)
    .createSignedUrl(toStoragePath(value), 60 * 60); // 1 time
  if (error) return null;
  return data?.signedUrl ?? null;
}

/**
 * React Query-wrapper rundt getTaskAttachmentUrl, cachet per path slik at
 * samme vedlegg ikke signeres på nytt ved hver render.
 */
export function useSignedAttachmentUrl(value: string | null) {
  return useQuery({
    queryKey: ["task-attachment-url", value],
    enabled: !!value,
    staleTime: 30 * 60 * 1000, // godt under signert URL-TTL (1t)
    queryFn: async (): Promise<string | null> => {
      if (!value) return null;
      return getTaskAttachmentUrl(value);
    },
  });
}

export function useCreateTaskComment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ assignmentId, userId, body, file }: CreateInput) => {
      const trimmed = body?.trim() || null;
      if (!trimmed && !file) throw new Error("Skriv noe eller legg ved en fil");
      const attachment = file ? await uploadAttachment(file, assignmentId, userId) : null;
      const { error } = await supabase.from("task_comments").insert({
        assignment_id: assignmentId,
        user_id: userId,
        body: trimmed,
        attachment_url: attachment?.url ?? null,
        attachment_name: attachment?.name ?? null,
        attachment_type: attachment?.type ?? null,
        attachment_size_bytes: attachment?.size ?? null,
      });
      if (error) throw error;

      // Varsel (task_comment_added) opprettes av DB-trigger på
      // task_comments-insert, ikke her.

      return { assignmentId };
    },
    onSuccess: async ({ assignmentId }) => {
      await invalidateFor(qc, "task_comments", { assignmentId });
    },
    onError: (err) => {
      toast.error(`Kunne ikke poste kommentar: ${(err as Error).message}`);
    },
  });
}

export function useDeleteTaskComment(assignmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (commentId: string) => {
      const { error } = await supabase
        .from("task_comments")
        .update({ deleted_at: new Date().toISOString() })
        .eq("id", commentId);
      if (error) throw error;
    },
    onSuccess: async () => {
      await invalidateFor(qc, "task_comments", { assignmentId });
    },
    onError: (err) => {
      toast.error(`Kunne ikke slette: ${(err as Error).message}`);
    },
  });
}

/**
 * Returnerer antall kommentarer per assignmentId, batched. Brukes av
 * BudgetLineRow / TaskRow til å vise et lite tall ved siden av oppgaven.
 */
export function useTaskCommentCounts(assignmentIds: string[]) {
  return useQuery({
    queryKey: ["task-comment-counts", assignmentIds.sort().join(",")],
    enabled: assignmentIds.length > 0,
    queryFn: async (): Promise<Map<string, number>> => {
      const { data, error } = await supabase
        .from("task_comments")
        .select("assignment_id")
        .in("assignment_id", assignmentIds)
        .is("deleted_at", null);
      if (error) throw error;
      const map = new Map<string, number>();
      for (const row of data ?? []) {
        map.set(row.assignment_id, (map.get(row.assignment_id) ?? 0) + 1);
      }
      return map;
    },
    staleTime: 30_000,
  });
}
